/**
 * MailVotes Postcard Mailer — customer-facing order flow.
 *
 * 5"x9" two-sided postcard mailer against a voter list:
 *   quote → save card (SetupIntent) → AI design proof → approve (charge) or
 *   request revision → send to printer. Card is saved at order and charged only
 *   at approval (off_session).
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useAuth } from '../contexts/AuthContext';
import { postcardService } from '../api/postcardService';
import { TEMPLATES, PostcardPreview } from '../components/PostcardTemplates';
import PostcardQuestionnaire, { EMPTY_QUESTIONNAIRE } from '../components/PostcardQuestionnaire';
import PostcardEditor, { Icon, designFromProof, fullAiOverlay, hydrateAiArt, DesignPreview } from '../components/PostcardEditor';
import { loadMailingListSearch, onMailingListSearchChange, describeMailingListSearch } from '../utils/mailingListSearch';

const STRIPE_PK =
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY_MAILVOTES ||
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
// Guard: in local dev the publishable key may be unset — loadStripe(undefined)
// throws and takes down the whole page. Elements accepts stripe={null}.
const stripePromise = STRIPE_PK ? loadStripe(STRIPE_PK) : null;

// MailVotes brand: navy primary, green accent (matches Header).
const PRIMARY = '#112030';
const ACCENT = '#4CAF50';
const card = { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 24, marginBottom: 20 };
const btn = (disabled, bg = PRIMARY) => ({ background: disabled ? '#94a3b8' : bg, color: '#fff', border: 'none', borderRadius: 8, padding: '10px 20px', fontSize: 15, fontWeight: 600, cursor: disabled ? 'default' : 'pointer' });
const btnOutline = { background: 'transparent', color: PRIMARY, border: `1px solid ${PRIMARY}`, borderRadius: 8, padding: '10px 18px', fontSize: 14, cursor: 'pointer' };
const input = { width: '100%', padding: '10px 12px', border: '1px solid #dee2e6', borderRadius: 8, fontSize: 14, boxSizing: 'border-box', marginBottom: 12 };
const label = { fontSize: 13, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 };

// Card-last flow: design first, payment only after the proof is approved.
const STEPS_AI = ['designing', 'proof_ready', 'charged', 'sent_to_printer'];
const STEPS_CUSTOM = ['awaiting_designer', 'proof_ready', 'charged', 'sent_to_printer'];
const STEP_LABEL = { created: 'Order placed', designing: 'Designing', awaiting_designer: 'With our designers', proof_ready: 'Proof ready', charged: 'Paid', sent_to_printer: 'Sent to printer' };
const ORDER_STATUS_LABEL = { ...STEP_LABEL, revision_requested: 'Revision requested', design_failed: 'Design failed', approving: 'Processing payment', charge_failed: 'Payment failed' };
// Orders a customer can pick back up after a reload.
const OPEN_ORDER_STATUSES = ['created', 'designing', 'awaiting_designer', 'revision_requested', 'design_failed', 'proof_ready', 'approving', 'charge_failed', 'charged'];

// Design-fee tiers — must mirror the server's DESIGN table (postcards.js).
const DESIGN_TIERS = {
  ai:     { fee: 99,  waiverQty: 500,  title: 'AI Design',     blurb: 'A unique two-sided design generated from your answers in minutes — real typography, your photo, unlimited-feel revisions.' },
  custom: { fee: 300, waiverQty: 2000, title: 'Custom Design', blurb: 'A designer builds your postcard by hand from the same questionnaire. Our team reaches out within one business day.' },
};

// ---- Gallery card with click-to-flip (front ⇄ back) -----------------------
const GalleryCard = ({ t, data, onSelect }) => {
  const [flipped, setFlipped] = useState(false);
  const [hover, setHover] = useState(false);
  return (
    <div style={{ borderRadius: 12, padding: 8, border: '1px solid #e0e0e0', background: '#fff',
      transition: 'transform .12s, box-shadow .12s', transform: hover ? 'translateY(-3px)' : 'none',
      boxShadow: hover ? '0 8px 22px rgba(0,0,0,0.14)' : 'none' }}
      onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}>
      {/* Flip stage — click to see the back */}
      <div onClick={() => setFlipped(f => !f)} title="Click to flip"
        style={{ perspective: 1000, cursor: 'pointer' }}>
        <div style={{ position: 'relative', transformStyle: 'preserve-3d', transition: 'transform .5s',
          transform: flipped ? 'rotateY(180deg)' : 'none', aspectRatio: '9 / 5' }}>
          <div style={{ position: 'absolute', inset: 0, backfaceVisibility: 'hidden' }}>
            <PostcardPreview templateId={t.id} side="front" data={data} />
          </div>
          <div style={{ position: 'absolute', inset: 0, backfaceVisibility: 'hidden', transform: 'rotateY(180deg)' }}>
            <PostcardPreview templateId={t.id} side="back" data={data} />
          </div>
        </div>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 4px 2px' }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: PRIMARY }}>{t.label}</span>
        <span style={{ fontSize: 11, color: '#94a3b8' }}>{flipped ? 'Back' : 'Front'} · tap to flip</span>
      </div>
      <button onClick={onSelect} style={{ ...btn(false), width: '100%', padding: '8px 0', fontSize: 13, marginTop: 2 }}>
        Personalize this design →
      </button>
    </div>
  );
};

// ---- Card-save step (SetupIntent) -----------------------------------------
const SaveCardForm = ({ order, onSaved, onError, buttonLabel, helperText }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setSaving(true);
    try {
      const result = await stripe.confirmCardSetup(order.setupIntentClientSecret, {
        payment_method: { card: elements.getElement(CardElement) },
      });
      if (result.error) throw new Error(result.error.message);
      await postcardService.confirmCard(order.orderId);
      onSaved();
    } catch (err) {
      onError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit}>
      <div style={{ ...input, padding: 14 }}>
        <CardElement options={{ style: { base: { fontSize: '16px' } } }} />
      </div>
      <p style={{ fontSize: 13, color: '#64748b', margin: '0 0 12px' }}>
        {helperText || <>Total: <b>${(order.quote.totalCents / 100).toFixed(2)}</b> ({order.quote.quantity} pieces).</>}
      </p>
      <button type="submit" disabled={saving || !stripe} style={btn(saving || !stripe, ACCENT)}>
        {saving ? 'Processing…' : (buttonLabel || 'Save card')}
      </button>
    </form>
  );
};

// ---- Main page ------------------------------------------------------------
const PostcardsPageInner = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [phase, setPhase] = useState('gallery'); // 'gallery' | 'design' | 'editor' | 'pricing' | 'questionnaire' | 'configure'
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [designType, setDesignType] = useState('ai'); // 'ai' | 'custom'
  const [questionnaire, setQuestionnaire] = useState(EMPTY_QUESTIONNAIRE);
  const [headshotB64, setHeadshotB64] = useState(null);
  // Saved Canva-editor design (element schema). Persisted to localStorage so a
  // refresh doesn't lose it; sent to the server with the order later.
  const [designJson, setDesignJson] = useState(() => {
    try { return JSON.parse(localStorage.getItem('mv_postcard_design') || 'null'); } catch { return null; }
  });
  useEffect(() => {
    // Image-heavy designs can exceed the ~5MB localStorage quota — best-effort.
    try { if (designJson) localStorage.setItem('mv_postcard_design', JSON.stringify(designJson)); } catch { /* quota */ }
  }, [designJson]);
  const [quantity, setQuantity] = useState(1000);
  const [candidateName, setCandidateName] = useState('');
  const [office, setOffice] = useState('');
  const [slogan, setSlogan] = useState('');
  const [party, setParty] = useState('R');
  const [message, setMessage] = useState('');
  const [designPrompt, setDesignPrompt] = useState('');
  const [flip, setFlip] = useState('front'); // preview side toggle

  // Personalization data fed live into the postcard template preview.
  const cardData = { candidateName, office, slogan, party, message,
    paidFor: candidateName ? `Paid for by the Committee to Elect ${candidateName}` : undefined };
  const [quote, setQuote] = useState(null);
  const [order, setOrder] = useState(null);      // { orderId, setupIntentClientSecret, quote }
  const [orderState, setOrderState] = useState(null); // full order doc after creation
  const [proof, setProof] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [note, setNote] = useState('');
  const [revNotes, setRevNotes] = useState('');
  // Multi-variant AI designs: previews returned by generate-variants; the
  // customer picks one (or opens it straight in the studio editor).
  const [variants, setVariants] = useState(null);
  const [varPrompt, setVarPrompt] = useState('');
  const [varBusy, setVarBusy] = useState(false);
  const [varChoosing, setVarChoosing] = useState(-1);
  // Design-first drafts (pre-order): clean art per variant for editor seeding,
  // chosen index rides along to order creation as chosenDraftIndex.
  const [draftArts, setDraftArts] = useState(null);
  const [draftRawArts, setDraftRawArts] = useState(null); // full_ai / nano_banana: unstamped art for editable-text seeding
  const [draftOverlays, setDraftOverlays] = useState(null); // server's measurement of each raw art
  // The element schema each AI preview was rendered from. Opening this is
  // what makes the editor show the card the customer actually picked.
  const [draftDesigns, setDraftDesigns] = useState(null);
  const [draftMode, setDraftMode] = useState('library');
  const [chosenDraft, setChosenDraft] = useState(null);
  const [photoBusy, setPhotoBusy] = useState(false); // headshot cutout in progress — locks navigation
  const [showPayment, setShowPayment] = useState(false); // card form revealed at approval time
  const [retryCard, setRetryCard] = useState(false); // card form after a declined charge
  // Who the mailer goes to: the customer's last voter search (Voters page).
  const [mailingSearch, setMailingSearch] = useState(() => loadMailingListSearch());
  const [listInfo, setListInfo] = useState(null); // { voterCount, maxQuantity } | { error }
  const [myOrders, setMyOrders] = useState([]); // open orders, so a reload doesn't lose them
  const [revising, setRevising] = useState(false); // "Request revision" → back to the questionnaire
  const [editingProof, setEditingProof] = useState(false); // proof opened in the studio editor

  // Live quote (print total + design fee, fee waived at the tier threshold)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try { const q = await postcardService.quote(quantity, designType); if (!cancelled) setQuote(q); }
      catch { if (!cancelled) setQuote(null); }
    })();
    return () => { cancelled = true; };
  }, [quantity, designType]);

  // A search run in another tab ("Choose voters") replaces the mail list.
  useEffect(() => onMailingListSearchChange(setMailingSearch), []);

  // Exact list size for the configure step; quantity defaults to the whole list.
  const signedInUid = currentUser?.uid || null;
  useEffect(() => {
    if (phase !== 'configure' || !signedInUid || !mailingSearch) { setListInfo(null); return undefined; }
    let cancelled = false;
    setListInfo(null);
    postcardService.listPreview(mailingSearch.filters)
      .then((info) => {
        if (cancelled) return;
        setListInfo(info);
        setQuantity(info.maxQuantity);
      })
      .catch((e) => { if (!cancelled) setListInfo({ error: e.message }); });
    return () => { cancelled = true; };
  }, [phase, signedInUid, mailingSearch]);

  // Orders already in flight for this customer.
  useEffect(() => {
    if (!signedInUid) return undefined;
    let cancelled = false;
    postcardService.listOrders()
      .then((list) => { if (!cancelled) setMyOrders((list || []).filter(o => OPEN_ORDER_STATUSES.includes(o.status))); })
      .catch(() => { /* the list is a convenience */ });
    return () => { cancelled = true; };
  }, [signedInUid]);

  const refreshOrder = useCallback(async (id) => {
    const o = await postcardService.getOrder(id);
    setOrderState(o);
    if (o.status === 'proof_ready' || o.status === 'charged' || o.status === 'sent_to_printer') {
      try { setProof(await postcardService.getProof(id)); } catch { /* no proof yet */ }
    }
    return o;
  }, []);

  // Picking a template is open to everyone — the buyer builds and personalizes
  // their postcard first. Only "Buy" or "Generate your own" require login.
  const requireLoginThen = (fn) => {
    if (!currentUser) { navigate('/login?next=/postcards'); return; }
    fn();
  };
  const pickTemplate = (t) => {
    setSelectedTemplate(t);
    setDesignPrompt(`Campaign postcard in the style of "${t.label}"`);
    setFlip('front');
    setPhase('design'); // build + personalize the postcard
  };
  // From the personalized postcard → questionnaire (template path is an AI order).
  const buyThis = () => requireLoginThen(() => { setDesignType('ai'); setPhase('questionnaire'); });
  // Or discard the template → choose AI vs custom design first (login-gated).
  const startOwnDesign = () => requireLoginThen(() => { setSelectedTemplate(null); setPhase('pricing'); });

  const createOrder = async () => {
    if (!currentUser) { navigate('/login?next=/postcards'); return; }
    if (!mailingSearch) { setErr('Choose who receives this mailer first.'); return; }
    setBusy(true); setErr('');
    try {
      // The customer's own voter search (validated and counted server-side).
      const searchParameters = mailingSearch.filters;
      const o = await postcardService.createOrder({
        searchParameters, quantity: Number(quantity),
        candidateName, office, message, slogan, party,
        designType, questionnaire,
        designPrompt: designPrompt || undefined,
        chosenDraftIndex: (!designJson && chosenDraft !== null) ? chosenDraft : undefined,
        templateId: selectedTemplate?.id,
      });
      setOrder(o);
      if (headshotB64) {
        try { await postcardService.uploadHeadshot(o.orderId, headshotB64); }
        catch (e) { console.warn('headshot upload failed:', e.message); }
      }
      // Card-last flow: the design starts right away; payment comes after the
      // proof is approved. A studio-edited design skips AI generation — the
      // server renders the design itself as the proof (and at print DPI).
      if (designType === 'ai' && designJson) {
        setNote('Order placed — preparing your design for print…');
        try { await postcardService.saveDesign(o.orderId, designJson); }
        catch (e) { setErr('Design save failed: ' + e.message); }
      } else if (designType === 'ai') {
        setNote(chosenDraft !== null
          ? 'Order placed with your chosen design — review the proof below.'
          : 'Order placed — generate your design below.');
      }
      await refreshOrder(o.orderId);
      if (designType === 'custom') setNote('Order placed — your brief is with our design team.');
      else if (!err) setNote('Proof ready — review front and back below.');
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  // Card saved at the END of the flow → immediately approve (charge). Legacy
  // orders that still start at 'created' fall back to the old design kick-off.
  const onCardSaved = async () => {
    setBusy(true); setErr('');
    try {
      const o = await refreshOrder(order.orderId);
      if (o.status === 'proof_ready' || o.status === 'charge_failed') {
        await postcardService.approve(order.orderId);
        await refreshOrder(order.orderId);
        setRetryCard(false);
        setNote('Payment complete. You can now send it to the printer.');
      } else if (o.status === 'designing') {
        setNote('Card saved — generate your design in the panel above.');
      } else {
        setNote('Card saved.');
      }
    } catch (e) {
      setErr(e.message);
      // Show where the order really is (charge_failed / approving), not a stale proof.
      try { await refreshOrder(order.orderId); } catch { /* keep the error */ }
    }
    finally { setBusy(false); }
  };

  // Pre-order generation (design-first). Library mode is free; full AI uses
  // the uploaded photo via PuLID.
  const genPreview = async (mode) => {
    setVarBusy(true); setErr('');
    try {
      const r = await postcardService.previewDesigns({
        designPrompt: varPrompt || designPrompt || undefined,
        mode: mode || undefined,
        candidateName, office, slogan, message, party, questionnaire,
        headshotB64: (mode === 'full_ai' || mode === 'nano_banana') ? headshotB64 : undefined,
      });
      setVariants(r.previews || []);
      setDraftArts(r.arts || []);
      setDraftRawArts(r.rawArts || []);
      setDraftOverlays(r.overlays || []);
      setDraftDesigns(r.designs || []);
      setDraftMode(r.mode || 'library');
      setChosenDraft(null);
    } catch (e) { setErr('Design generation failed: ' + e.message); }
    finally { setVarBusy(false); }
  };

  const applyDraft = (i) => {
    setChosenDraft(i);
    setDesignJson(null); // the chosen draft IS the design
    setPhase('configure');
    setNote('Design locked in — set your quantity below.');
  };

  const editDraft = (i) => {
    const art = draftArts?.[i];
    if (!art) return;
    const d = { candidateName, office, slogan, message, party, questionnaire };
    let seeded;
    // An AI card comes back with the very schema its preview was rendered from.
    // Opening that is what makes "edit this card" show the card that was
    // previewed; rebuilding the copy here from the client's own constants is
    // what used to make the two disagree.
    const serverDesign = draftDesigns?.[i];
    const raw = draftRawArts?.[i];
    if (serverDesign && raw) {
      seeded = { front: hydrateAiArt(serverDesign, raw), back: designFromProof({ artFront: art, watermarked: false }, selectedTemplate?.id || 'stars', d, null).back };
    } else if (draftMode === 'nano_banana' || draftMode === 'full_ai') {
      // Cards generated before the server sent its schema: seed the background
      // from the text-free pass and rebuild the copy as before.
      seeded = designFromProof({ artFront: raw || art, watermarked: false },
        selectedTemplate?.id || 'stars', d, null);
      seeded.front = seeded.front.filter(el => el.role === 'background');
      if (raw) seeded.front = [...seeded.front, ...fullAiOverlay(d, draftOverlays?.[i] || null)];
    } else {
      seeded = designFromProof({ artFront: art, watermarked: false },
        selectedTemplate?.id || 'stars', d, headshotB64);
    }
    setDesignJson(seeded);
    setChosenDraft(null);
    setPhase('editor');
  };

  const genVariants = async (mode) => {
    setVarBusy(true); setErr('');
    try {
      const r = await postcardService.generateVariants(order.orderId, varPrompt || designPrompt, mode);
      setVariants(r.previews || []);
      // Kept so "Edit" opens the text-free pass with live copy rather than a
      // locked picture — the same seeding editDraft does.
      setDraftArts(r.arts || []);
      setDraftRawArts(r.rawArts || []);
      setDraftOverlays(r.overlays || []);
      setDraftDesigns(r.designs || []);
      setDraftMode(r.mode || mode || 'library');
      setNote(mode === 'full_ai' || mode === 'nano_banana'
        ? 'AI cards ready — your name and face are on the design. Pick or edit one below.'
        : 'Pick a design below — or open one in the studio to edit it.');
    } catch (e) { setErr('Design generation failed: ' + e.message); }
    finally { setVarBusy(false); }
  };

  const pickVariant = async (i, edit) => {
    setVarChoosing(i); setErr('');
    try {
      await postcardService.chooseVariant(order.orderId, i);
      await refreshOrder(order.orderId);
      if (edit) {
        const raw = draftRawArts?.[i];
        const serverDesign = draftDesigns?.[i];
        const d = { candidateName, office, slogan, message, party, questionnaire };
        if (serverDesign && raw) {
          // The schema the chosen preview was rendered from — open it as-is.
          const base = designFromProof({ artFront: raw, watermarked: false },
            selectedTemplate?.id || orderState?.templateId || 'stars', d, null);
          setDesignJson({ front: hydrateAiArt(serverDesign, raw), back: base.back });
        } else if (raw) {
          // Pre-schema cards: rebuild the copy over the text-free pass.
          const seeded = designFromProof({ artFront: raw, watermarked: false },
            selectedTemplate?.id || orderState?.templateId || 'stars', d, null);
          seeded.front = seeded.front.filter(el => el.role === 'background');
          seeded.front = [...seeded.front, ...fullAiOverlay(d, draftOverlays?.[i] || null)];
          setDesignJson(seeded);
        } else {
          const p2 = await postcardService.getProof(order.orderId);
          setProof(p2);
          setDesignJson(designFromProof(p2, selectedTemplate?.id || orderState?.templateId,
            d, headshotB64));
        }
        setEditingProof(true);
      } else {
        setNote('Design selected — review your proof below.');
      }
      setVariants(null);
    } catch (e) { setErr(e.message); }
    finally { setVarChoosing(-1); }
  };

  const regenerate = async () => {
    setBusy(true); setErr('');
    try {
      if (orderState.status === 'proof_ready') await postcardService.requestRevision(order.orderId, revNotes);
      await refreshOrder(order.orderId);
      setRevNotes(''); setVariants(null);
      setNote('Generate new designs in the panel above.');
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  // Revision flow: the edited questionnaire is saved to the order, then the
  // design regenerates and the customer lands back on the new proof.
  const submitRevision = async () => {
    setBusy(true); setErr('');
    try {
      await postcardService.updateOrder(order.orderId, { questionnaire, candidateName, office, party });
      if (headshotB64) {
        try { await postcardService.uploadHeadshot(order.orderId, headshotB64); }
        catch (e) { console.warn('headshot upload failed:', e.message); }
      }
      await postcardService.requestRevision(order.orderId, revNotes);
      await refreshOrder(order.orderId);
      setRevising(false); setRevNotes('');
      setVariants(null);
      setNote('Notes saved — generate new designs below.');
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  const approve = async () => {
    setBusy(true); setErr('');
    try {
      await postcardService.approve(order.orderId);
      await refreshOrder(order.orderId);
      setNote('Payment charged. You can now send it to the printer.');
    } catch (e) {
      setErr('Approve/charge failed: ' + e.message);
      try { await refreshOrder(order.orderId); } catch { /* keep the error */ }
    }
    finally { setBusy(false); }
  };

  // 'approving': ask the server again. It answers "still processing", or
  // finishes an approval that was interrupted (never charging twice).
  const checkApproval = async () => {
    setBusy(true); setErr('');
    try {
      const o = await refreshOrder(order.orderId);
      if (o.status === 'approving') {
        try {
          await postcardService.approve(order.orderId);
          await refreshOrder(order.orderId);
          setNote('Payment complete. You can now send it to the printer.');
        } catch (e) {
          if (e.code === 'approval_in_progress') setNote('Still processing — check again in a minute.');
          else setErr(e.message);
          try { await refreshOrder(order.orderId); } catch { /* keep the message */ }
        }
      }
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  // A card form needs a SetupIntent secret; reopened or declined orders get a fresh one.
  const ensureSetupIntent = async () => {
    if (order.setupIntentClientSecret && !retryCard && status !== 'charge_failed') return true;
    try {
      const r = await postcardService.newSetupIntent(order.orderId);
      setOrder(o => ({ ...o, setupIntentClientSecret: r.setupIntentClientSecret }));
      return true;
    } catch (e) { setErr(e.message); return false; }
  };

  const openPayment = async () => {
    setBusy(true); setErr('');
    const ok = await ensureSetupIntent();
    setBusy(false);
    if (ok) setShowPayment(true);
  };

  const startCardRetry = async () => {
    setBusy(true); setErr('');
    const ok = await ensureSetupIntent();
    setBusy(false);
    if (ok) setRetryCard(true);
  };

  const openOrder = async (o) => {
    setBusy(true); setErr(''); setNote('');
    try {
      setOrder({ orderId: o.id, quote: { totalCents: o.priceCents, quantity: o.quantity } });
      if (o.designType) setDesignType(o.designType);
      if (o.questionnaire) setQuestionnaire(q => ({ ...q, ...o.questionnaire }));
      if (o.candidateName) setCandidateName(o.candidateName);
      if (o.office) setOffice(o.office);
      if (o.party) setParty(o.party);
      await refreshOrder(o.id);
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  const sendToPrinter = async () => {
    setBusy(true); setErr('');
    try {
      const r = await postcardService.sendToPrinter(order.orderId);
      await refreshOrder(order.orderId);
      setNote(r.printerSend?.sent ? 'Sent to printer!' : `Recorded (printer email off: ${r.printerSend?.reason}).`);
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  const status = orderState?.status;

  return (
    <div className="content-section" style={{ maxWidth: 1100, marginLeft: 'auto', marginRight: 'auto', marginTop: 88 }}>
      <h1 style={{ fontSize: 28, marginTop: 0, marginBottom: 4 }}>Postcard Mailer</h1>
      <p style={{ color: '#64748b', marginTop: 0 }}>
        Order a large-format <b>5" × 9" two-sided postcard</b> mailer to your voter list. Design → approve → we print & mail it.
      </p>

      {err && <div style={{ ...card, background: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca' }}>{err}</div>}
      {note && !err && <div style={{ ...card, background: '#f0fdf4', color: '#166534', border: '1px solid #bbf7d0' }}>{note}</div>}

      {/* Status tracker */}
      {orderState && (
        <div style={{ ...card, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {(orderState.designType === 'custom' ? STEPS_CUSTOM : STEPS_AI).map((s, i, STEPS) => {
            const reached = STEPS.indexOf(status) >= i;
            return (
              <div key={s} style={{ flex: 1, minWidth: 110, textAlign: 'center', opacity: reached ? 1 : 0.4 }}>
                <div style={{ height: 6, borderRadius: 3, background: reached ? PRIMARY : '#e2e8f0', marginBottom: 6 }} />
                <span style={{ fontSize: 12, fontWeight: reached ? 700 : 500 }}>{STEP_LABEL[s]}</span>
              </div>
            );
          })}
        </div>
      )}

      {/* Orders already in flight — a reload used to lose them */}
      {!order && phase === 'gallery' && myOrders.length > 0 && (
        <div style={card}>
          <h3 style={{ marginTop: 0 }}>Pick up where you left off</h3>
          {myOrders.map(o => (
            <div key={o.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, padding: '10px 0', borderTop: '1px solid #f1f5f9', flexWrap: 'wrap' }}>
              <span style={{ fontSize: 14, color: '#334155' }}>
                {o.candidateName || 'Postcard mailer'} · {Number(o.quantity || 0).toLocaleString()} pieces · ${((o.priceCents || 0) / 100).toFixed(2)} · <b>{ORDER_STATUS_LABEL[o.status] || o.status}</b>
              </span>
              <button onClick={() => openOrder(o)} disabled={busy} style={{ ...btnOutline, padding: '6px 12px', fontSize: 13 }}>Open</button>
            </div>
          ))}
        </div>
      )}

      {/* Step 0: template gallery — real postcard designs, personalized live */}
      {!order && phase === 'gallery' && (
        <div style={card}>
          <h3 style={{ marginTop: 0 }}>Choose a postcard design</h3>
          <p style={{ color: '#64748b', marginTop: 0, fontSize: 14 }}>
            Pick a professionally designed 5" × 9" mailer — you'll personalize it with your name and message next.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: 18, marginTop: 16 }}>
            {TEMPLATES.map(t => (
              <GalleryCard key={t.id} t={t} data={cardData} onSelect={() => pickTemplate(t)} />
            ))}
          </div>
          <div style={{ marginTop: 22, paddingTop: 16, borderTop: '1px solid #eee', display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <button onClick={startOwnDesign} style={{ ...btn(false, ACCENT), display: 'inline-flex', alignItems: 'center', gap: 7 }}><Icon name="sparkles" size={15} /> Generate my own design</button>
            <span style={{ fontSize: 13, color: '#94a3b8' }}>Prefer something unique? Create a custom two-sided postcard with AI{!currentUser && ' (sign-in required)'}.</span>
          </div>
        </div>
      )}

      {/* Step 0.5: build + personalize the chosen postcard, then Buy / Generate own */}
      {!order && phase === 'design' && selectedTemplate && (
        <div style={card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <h3 style={{ margin: 0 }}>Personalize your postcard</h3>
            <button onClick={() => setPhase('gallery')} style={{ ...btnOutline, padding: '6px 12px', fontSize: 13 }}>← All designs</button>
          </div>

          {/* Live front/back preview */}
          <div style={{ display: 'flex', gap: 12, marginBottom: 10 }}>
            <button onClick={() => setFlip('front')} style={{ ...(flip === 'front' ? btn(false) : btnOutline), padding: '6px 14px', fontSize: 13 }}>Front</button>
            <button onClick={() => setFlip('back')} style={{ ...(flip === 'back' ? btn(false) : btnOutline), padding: '6px 14px', fontSize: 13 }}>Back</button>
            <span style={{ alignSelf: 'center', fontSize: 12, color: '#94a3b8' }}>{selectedTemplate.label} · 5" × 9" two-sided</span>
          </div>
          <div style={{ maxWidth: 560, margin: '0 auto 18px' }}>
            <PostcardPreview templateId={selectedTemplate.id} side={flip} data={cardData} />
          </div>

          {/* Personalization fields */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
            <div>
              <label style={label}>Candidate name</label>
              <input style={input} value={candidateName} onChange={e => setCandidateName(e.target.value)} placeholder="e.g. Jane Smith" />
            </div>
            <div>
              <label style={label}>Office</label>
              <input style={input} value={office} onChange={e => setOffice(e.target.value)} placeholder="e.g. for Mayor" />
            </div>
            <div>
              <label style={label}>Slogan</label>
              <input style={input} value={slogan} onChange={e => setSlogan(e.target.value)} placeholder="e.g. Leadership that works" />
            </div>
            <div>
              <label style={label}>Party</label>
              <select style={{ ...input, cursor: 'pointer' }} value={party} onChange={e => setParty(e.target.value)}>
                <option value="R">Republican (red)</option>
                <option value="D">Democrat (blue)</option>
                <option value="I">Independent (navy)</option>
              </select>
            </div>
          </div>
          <div>
            <label style={label}>Back message (optional)</label>
            <input style={input} value={message} onChange={e => setMessage(e.target.value)} placeholder="A short message printed on the back" />
          </div>

          <div style={{ marginTop: 8, paddingTop: 16, borderTop: '1px solid #eee', display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <button onClick={buyThis} style={btn(false, ACCENT)}>Buy this design →</button>
            <button onClick={() => setPhase('editor')} style={{ ...btnOutline, display: 'inline-flex', alignItems: 'center', gap: 7 }}><Icon name="edit" size={14} /> Customize in editor</button>
            <button onClick={startOwnDesign} style={{ ...btnOutline, display: 'inline-flex', alignItems: 'center', gap: 7 }}><Icon name="sparkles" size={14} /> Generate my own instead</button>
            {designJson && <span style={{ fontSize: 13, color: '#166534', fontWeight: 600 }}>✓ Custom design saved</span>}
            {!currentUser && <span style={{ fontSize: 13, color: '#94a3b8' }}>Sign in required to continue.</span>}
          </div>
        </div>
      )}

      {/* Step 0.55: Canva-style live design editor (element schema, 1024×576) */}
      {!order && phase === 'editor' && (
        <PostcardEditor
          templateId={selectedTemplate?.id}
          data={{ candidateName, office, slogan, message, party, questionnaire }}
          initialDesign={designJson}
          headshotB64={headshotB64}
          onHeadshot={setHeadshotB64}
          onSave={(d) => { setDesignJson(d); setPhase(variants ? 'configure' : 'design'); setNote('Design saved — it will be used for your order.'); }}
          onCancel={() => setPhase(variants ? 'generate' : 'design')}
        />
      )}

      {/* Step 0.6: choose AI vs custom design (pricing) */}
      {!order && phase === 'pricing' && (
        <div style={card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
            <h3 style={{ margin: 0 }}>How should we design your postcard?</h3>
            <button onClick={() => setPhase('gallery')} style={{ ...btnOutline, padding: '6px 12px', fontSize: 13 }}>← All designs</button>
          </div>
          <p style={{ color: '#64748b', marginTop: 6, fontSize: 14 }}>One design fee per order — <b>waived automatically</b> when you print and mail enough postcards.</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>
            {Object.entries(DESIGN_TIERS).map(([key, t]) => (
              <div key={key} style={{ border: `2px solid ${designType === key ? ACCENT : '#e2e8f0'}`, borderRadius: 12, padding: 20 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                  <span style={{ fontSize: 18, fontWeight: 800, color: PRIMARY }}>{t.title}</span>
                  <span style={{ fontSize: 22, fontWeight: 800, color: PRIMARY }}>${t.fee}</span>
                </div>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#166534', margin: '4px 0 10px' }}>
                  FREE with {t.waiverQty.toLocaleString()}+ postcards printed &amp; mailed
                </div>
                <p style={{ fontSize: 14, color: '#475569', minHeight: 66, marginTop: 0 }}>{t.blurb}</p>
                <button onClick={() => { setDesignType(key); setPhase('questionnaire'); }} style={{ ...btn(false, key === 'ai' ? ACCENT : PRIMARY), width: '100%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 7 }}>
                  {key === 'ai' ? <><Icon name="sparkles" size={15} /> Start with AI</> : 'Work with a designer'}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Step 0.7: design questionnaire (both AI and custom). Also shown when
          the customer presses "Request revision" on the proof — they come back
          here, edit the brief, and regenerate. */}
      {((!order && phase === 'questionnaire') || (order && revising)) && (
        <div style={card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <h3 style={{ margin: 0 }}>{revising ? 'Revise your design brief' : 'Tell us about your campaign'}</h3>
            {revising
              ? <button onClick={() => setRevising(false)} style={{ ...btnOutline, padding: '6px 12px', fontSize: 13 }}>← Back to proof</button>
              : <button onClick={() => setPhase(selectedTemplate ? 'design' : 'pricing')} style={{ ...btnOutline, padding: '6px 12px', fontSize: 13 }}>← Back</button>}
          </div>
          <p style={{ color: '#64748b', marginTop: 0, fontSize: 14 }}>
            {revising
              ? 'Adjust anything below — the design will be regenerated from your updated answers.'
              : designType === 'custom'
                ? 'This brief goes straight to your designer.'
                : 'Your answers drive the artwork and the printed copy — the AI never invents facts about you.'}
          </p>
          {(revising || !selectedTemplate) && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
              <div>
                <label style={label}>Candidate name</label>
                <input style={input} value={candidateName} onChange={e => setCandidateName(e.target.value)} placeholder="e.g. Jane Smith" />
              </div>
              <div>
                <label style={label}>Office</label>
                <input style={input} value={office} onChange={e => setOffice(e.target.value)} placeholder="e.g. County Commissioner, District 3" />
              </div>
              <div>
                <label style={label}>Party</label>
                <select style={{ ...input, cursor: 'pointer' }} value={party} onChange={e => setParty(e.target.value)}>
                  <option value="R">Republican</option>
                  <option value="D">Democrat</option>
                  <option value="I">Independent</option>
                </select>
              </div>
            </div>
          )}
          <PostcardQuestionnaire values={questionnaire} onChange={setQuestionnaire} headshotB64={headshotB64} onHeadshot={setHeadshotB64} onPhotoBusy={setPhotoBusy} />
          {revising && (
            <>
              <label style={label}>What should change about the current design? (optional)</label>
              <input style={input} value={revNotes} onChange={e => setRevNotes(e.target.value)} placeholder="e.g. make the background blue, larger name" />
            </>
          )}
          {revising ? (
            <button onClick={submitRevision} disabled={busy || photoBusy || !questionnaire.keyMessage.trim()} style={btn(busy || photoBusy || !questionnaire.keyMessage.trim(), ACCENT)}>
              {busy ? 'Regenerating…' : photoBusy ? 'Processing photo…' : 'Regenerate design →'}
            </button>
          ) : (
            <button onClick={() => setPhase(designType === 'ai' ? 'generate' : 'configure')} disabled={photoBusy || !questionnaire.keyMessage.trim()} style={btn(photoBusy || !questionnaire.keyMessage.trim())}>
              {photoBusy ? 'Processing photo…' : 'Continue to quantity & pricing →'}
            </button>
          )}
          {!questionnaire.keyMessage.trim() && <span style={{ fontSize: 13, color: '#94a3b8', marginLeft: 10 }}>The key message is required.</span>}
        </div>
      )}

      {/* Step 1: configure + quote (before order exists) */}
      {/* Design-first generation — before any order exists */}
      {!order && phase === 'generate' && (
        <div style={card}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
            <button onClick={() => setPhase('questionnaire')} disabled={varBusy}
              style={{ ...btnOutline, padding: '6px 12px', fontSize: 13, opacity: varBusy ? 0.5 : 1, cursor: varBusy ? 'default' : 'pointer' }}>← Back</button>
            <h3 style={{ margin: 0 }}>Generate your design</h3>
          </div>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 6 }}>
            Describe the background you want <span style={{ color: '#94a3b8', fontWeight: 400 }}>(optional)</span>
          </label>
          <input style={{ ...input, marginBottom: 10 }} value={varPrompt} disabled={varBusy}
            onChange={e => setVarPrompt(e.target.value)}
            placeholder="e.g. sunset over wheat fields, warm optimistic tones" />
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
            <button onClick={() => genPreview('nano_banana')} disabled={varBusy || !headshotB64}
              title={headshotB64 ? 'The AI paints the whole card around your photo and slogan' : 'Upload a candidate photo first'}
              style={{ ...btn(varBusy || !headshotB64), display: 'inline-flex', alignItems: 'center', gap: 7 }}>
              <Icon name="sparkles" size={14} /> {varBusy ? 'Generating…' : variants ? 'Regenerate designs' : 'Generate my designs'}
            </button>
            {!headshotB64 && (
              <span style={{ fontSize: 13, color: '#94a3b8' }}>Upload a candidate photo in the previous step first.</span>
            )}
          </div>
          {varBusy && <div style={{ fontSize: 13, color: '#64748b', marginTop: 8 }}>Designing your cards — usually 15–30 seconds.</div>}
          {variants && !varBusy && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 14, marginTop: 16 }}>
              {variants.map((b64, i) => (
                <div key={i} style={{ border: chosenDraft === i ? `2px solid ${ACCENT}` : '1px solid #e2e8f0', borderRadius: 10, overflow: 'hidden', background: '#fff' }}>
                  <img src={`data:image/jpeg;base64,${b64}`} alt={`design ${i + 1}`} style={{ width: '100%', display: 'block' }} />
                  <div style={{ display: 'flex', gap: 8, padding: 10 }}>
                    <button onClick={() => applyDraft(i)} style={{ ...btn(false, ACCENT), flex: 1, padding: '8px 0', fontSize: 13 }}>
                      Use this design →
                    </button>
                    <button onClick={() => editDraft(i)} style={{ ...btnOutline, flex: 1, padding: '8px 0', fontSize: 13, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                      <Icon name="edit" size={13} /> Edit
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {!order && phase === 'configure' && (
        <div style={card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3 style={{ marginTop: 0, marginBottom: 8 }}>1. Configure your mailer</h3>
            <button onClick={() => setPhase('questionnaire')} style={{ ...btnOutline, padding: '6px 12px', fontSize: 13 }}>← Back</button>
          </div>
          {selectedTemplate && (
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', background: '#f9f9f9', borderRadius: 8, padding: 10, marginBottom: 14 }}>
              <div style={{ width: 120, flexShrink: 0 }}><PostcardPreview templateId={selectedTemplate.id} side="front" data={cardData} /></div>
              <div style={{ fontSize: 13, color: '#475569' }}>Ordering the <b>{selectedTemplate.label}</b> design for <b>{candidateName || 'your candidate'}</b>. Set quantity below.</div>
            </div>
          )}
          {/* Who receives it: the customer's own voter search */}
          <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: 12, marginBottom: 14 }}>
            <div style={label}>Mailing list</div>
            {mailingSearch ? (
              <>
                <div style={{ fontSize: 14, color: PRIMARY }}>{describeMailingListSearch(mailingSearch.filters)}</div>
                <div style={{ fontSize: 13, color: '#475569', marginTop: 4 }}>
                  {listInfo?.error
                    ? <span style={{ color: '#991b1b' }}>{listInfo.error}</span>
                    : listInfo
                      ? <><b>{listInfo.voterCount.toLocaleString()}</b> voters in this list</>
                      : 'Counting voters…'}
                </div>
              </>
            ) : (
              <div style={{ fontSize: 13, color: '#475569' }}>
                Choose who receives this mailer: run a voter search, then come back to this tab.
              </div>
            )}
            <button
              onClick={() => window.open('/voters', '_blank', 'noopener')}
              style={{ ...btnOutline, padding: '6px 12px', fontSize: 13, marginTop: 8 }}
            >
              {mailingSearch ? 'Change list' : 'Choose voters'}
            </button>
          </div>

          <label style={label}>Quantity (pieces)</label>
          <input style={input} type="number" min={1} max={listInfo?.maxQuantity || undefined} value={quantity} onChange={e => setQuantity(e.target.value)} />
          {listInfo?.maxQuantity && Number(quantity) > listInfo.maxQuantity && (
            <div style={{ fontSize: 13, color: '#991b1b', marginTop: -6, marginBottom: 10 }}>
              Your list has {listInfo.maxQuantity.toLocaleString()} voters — you can't mail more pieces than that.
            </div>
          )}

          {/* Optional advanced art direction (AI path only) */}
          {designType === 'ai' && !selectedTemplate && (
            <>
              <label style={label}>Art direction (optional — overrides the automatic prompt)</label>
              <input style={input} value={designPrompt} onChange={e => setDesignPrompt(e.target.value)} placeholder="Describe the artwork you want" />
            </>
          )}

          {quote && (
            <div style={{ background: '#f8fafc', borderRadius: 8, padding: 14, margin: '8px 0 16px' }}>
              <div style={{ fontSize: 22, fontWeight: 700 }}>${quote.totalDollars}</div>
              <div style={{ fontSize: 13, color: '#334155', marginTop: 6 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', maxWidth: 380 }}>
                  <span>Print &amp; mail — {quote.quantity} × ${(quote.perPieceCents / 100).toFixed(2)}</span>
                  <b>${((quote.printTotalCents ?? quote.totalCents) / 100).toFixed(2)}</b>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', maxWidth: 380 }}>
                  <span>{DESIGN_TIERS[quote.designType || designType]?.title || 'Design'} fee</span>
                  {quote.designFeeWaived
                    ? <b style={{ color: '#166534' }}><s style={{ color: '#94a3b8', fontWeight: 400 }}>${((quote.designFeeListCents || 0) / 100).toFixed(0)}</s> FREE</b>
                    : <b>${((quote.designFeeCents || 0) / 100).toFixed(2)}</b>}
                </div>
              </div>
              {!quote.designFeeWaived && quote.waiverQty && (
                <div style={{ fontSize: 13, color: '#166534', fontWeight: 600, marginTop: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Icon name="info" size={14} /> Order {(quote.waiverQty - quote.quantity).toLocaleString()} more postcards and the ${((quote.designFeeListCents || 0) / 100).toFixed(0)} design fee is free.
                </div>
              )}
              <div style={{ fontSize: 12, color: '#64748b', marginTop: 8 }}>
                First-Class <b>Letter</b> rate (5×9 exceeds postcard size; non-machinable surcharge included)
              </div>
              <div style={{ fontSize: 13, color: '#166534', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '8px 12px', marginTop: 10, display: 'flex', alignItems: 'center', gap: 7 }}>
                <Icon name="info" size={14} /> <span><b>Satisfaction guaranteed.</b> Nothing is charged until you approve your proof — and if you're not happy with the design, we refund the design fee.</span>
              </div>
            </div>
          )}
          {(() => {
            const listReady = !!(mailingSearch && listInfo && !listInfo.error);
            const tooMany = listInfo?.maxQuantity && Number(quantity) > listInfo.maxQuantity;
            const blocked = busy || !quote || !listReady || tooMany;
            return (
              <button onClick={createOrder} disabled={blocked} style={btn(blocked)}>
                {busy ? 'Working…' : 'Place order'}
              </button>
            );
          })()}
        </div>
      )}

      {/* Legacy orders only — new orders never start at 'created' (card-last flow) */}
      {order && status === 'created' && (
        <div style={card}>
          <h3 style={{ marginTop: 0 }}>Save your card</h3>
          <SaveCardForm order={order} onSaved={onCardSaved} onError={setErr} buttonLabel="Save card & continue" />
        </div>
      )}

      {/* Custom order queued for the human design team */}
      {order && status === 'awaiting_designer' && (
        <div style={{ ...card, background: '#eff6ff', border: '1px solid #bfdbfe' }}>
          <h3 style={{ marginTop: 0, display: 'flex', alignItems: 'center', gap: 8 }}><Icon name="edit" size={17} /> Your brief is with our design team</h3>
          <p style={{ margin: 0, color: '#334155' }}>
            We'll reach out within one business day to walk through your questionnaire and start the custom design.
            You'll only be asked for payment after you approve the final proof.
          </p>
        </div>
      )}

      {/* AI designs — prompt-steerable, several variants per click, each with
          its own Edit button that opens it in the studio. */}
      {order && (status === 'designing' || status === 'revision_requested' || status === 'design_failed') && !editingProof && (
        <div style={card}>
          <h3 style={{ marginTop: 0 }}>3. Generate your design</h3>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 6 }}>
            Describe the background you want <span style={{ color: '#94a3b8', fontWeight: 400 }}>(optional — steers the artwork)</span>
          </label>
          <input
            style={{ ...input, marginBottom: 10 }}
            value={varPrompt}
            onChange={e => setVarPrompt(e.target.value)}
            placeholder="e.g. sunset over wheat fields, warm optimistic tones"
          />
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
            <button onClick={() => genVariants('nano_banana')} disabled={varBusy || varChoosing >= 0 || !headshotB64}
              title={headshotB64 ? 'The AI paints the whole card around your photo and slogan' : 'Upload a candidate photo first'}
              style={{ ...btn(varBusy || varChoosing >= 0 || !headshotB64), display: 'inline-flex', alignItems: 'center', gap: 7 }}>
              <Icon name="sparkles" size={14} /> {varBusy ? 'Generating…' : variants ? 'Regenerate designs' : 'Generate my designs'}
            </button>
            {!headshotB64 && (
              <span style={{ fontSize: 13, color: '#94a3b8' }}>Add a candidate photo in the personalization step first.</span>
            )}
          </div>
          {varBusy && (
            <div style={{ fontSize: 13, color: '#64748b', marginTop: 8 }}>
              Designing your cards — usually 15–30 seconds.
            </div>
          )}
          {variants && !varBusy && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 14, marginTop: 16 }}>
              {variants.map((b64, i) => (
                <div key={i} style={{ border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'hidden', background: '#fff' }}>
                  <img src={`data:image/jpeg;base64,${b64}`} alt={`design ${i + 1}`} style={{ width: '100%', display: 'block' }} />
                  <div style={{ display: 'flex', gap: 8, padding: 10 }}>
                    <button onClick={() => pickVariant(i, false)} disabled={varChoosing >= 0}
                      style={{ ...btn(varChoosing === i), flex: 1, padding: '8px 0', fontSize: 13 }}>
                      {varChoosing === i ? 'Selecting…' : 'Use this design'}
                    </button>
                    <button onClick={() => pickVariant(i, true)} disabled={varChoosing >= 0}
                      style={{ ...btnOutline, flex: 1, padding: '8px 0', fontSize: 13, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                      <Icon name="edit" size={13} /> Edit
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Proof opened in the studio editor — the AI art becomes the background
          layer and the copy stays editable on top. */}
      {order && editingProof && proof && (
        <PostcardEditor
          templateId={selectedTemplate?.id || orderState?.templateId}
          data={{ candidateName, office, slogan, message, party, questionnaire }}
          initialDesign={designJson}
          headshotB64={headshotB64}
          onHeadshot={setHeadshotB64}
          onSave={async (d) => {
            setDesignJson(d); setEditingProof(false); setBusy(true);
            try {
              // Push to the server so the rendered proof + print file match
              // exactly what was edited — the local preview alone never prints.
              await postcardService.saveDesign(order.orderId, d);
              await refreshOrder(order.orderId);
              setNote('Design saved. Approve it below when you are ready.');
            } catch (e) { setErr('Design save failed: ' + e.message); }
            finally { setBusy(false); }
          }}
          onCancel={() => setEditingProof(false)}
        />
      )}

      {/* Proof review */}
      {order && status === 'proof_ready' && proof && !revising && !editingProof && (
        <div style={card}>
          <h3 style={{ marginTop: 0 }}>3. Review your proof</h3>
          {designJson && (
            <div style={{ fontSize: 13, color: '#166534', fontWeight: 600, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
              <Icon name="check-circle" size={14} /> Showing your edited design — it will be used for print.
            </div>
          )}
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            {['front', 'back'].map(side => (
              <div key={side} style={{ flex: '1 1 320px' }}>
                <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6, textTransform: 'uppercase', color: '#475569' }}>{side}</div>
                {designJson
                  ? <div style={{ borderRadius: 8, border: '1px solid #e2e8f0', overflow: 'hidden' }}><DesignPreview list={designJson[side]} /></div>
                  : proof[side]
                    ? <img alt={side} src={`data:image/jpeg;base64,${proof[side]}`} style={{ width: '100%', borderRadius: 8, border: '1px solid #e2e8f0' }} />
                    : <div style={{ padding: 40, background: '#f1f5f9', borderRadius: 8, textAlign: 'center', color: '#94a3b8' }}>No image</div>}
              </div>
            ))}
          </div>
          <div style={{ marginTop: 16 }}>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              {orderState.stripePaymentMethodId
                ? <button onClick={approve} disabled={busy} style={btn(busy, ACCENT)}>Approve &amp; pay ${(orderState.priceCents / 100).toFixed(2)}</button>
                : !showPayment && <button onClick={openPayment} disabled={busy} style={btn(busy, ACCENT)}>Approve — continue to payment →</button>}
              <div style={{ fontSize: 12.5, color: '#64748b', marginTop: 8 }}>
                Money-back guarantee on the design fee — approve only when the proof looks right. The preview watermark disappears from the printed cards.
              </div>
              {/* Tweak the proof directly instead of regenerating it */}
              <button
                onClick={() => {
                  setShowPayment(false);
                  // Seed the editor from the rendered proof the first time.
                  if (!designJson) {
                    const d = { candidateName, office, slogan, message, party, questionnaire };
                    const base = designFromProof(proof, selectedTemplate?.id || orderState?.templateId, d, headshotB64);
                    // An AI proof carries the schema it was rendered from. This
                    // path used to ignore it and seed the whole 'stars'
                    // template over the artwork instead — a name plate, an
                    // accent bar and a second copy of the candidate's photo on
                    // a card that already had one.
                    setDesignJson(proof?.frontDesign && proof?.artFront
                      ? { front: hydrateAiArt(proof.frontDesign, proof.artFront), back: base.back }
                      : base);
                  }
                  setEditingProof(true);
                }}
                disabled={busy}
                style={{ ...btnOutline, display: 'inline-flex', alignItems: 'center', gap: 7 }}>
                <Icon name="edit" size={14} /> Edit in studio
              </button>
              {/* Back to the questionnaire so the whole brief can be adjusted */}
              <button onClick={() => { setShowPayment(false); setRevising(true); }} disabled={busy} style={btnOutline}>Request revision</button>
            </div>
            {!orderState.stripePaymentMethodId && showPayment && order.setupIntentClientSecret && (
              <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #eee', maxWidth: 480 }}>
                <h4 style={{ margin: '0 0 10px' }}>Payment</h4>
                <SaveCardForm
                  order={order}
                  onSaved={onCardSaved}
                  onError={setErr}
                  buttonLabel={`Pay $${(orderState.priceCents / 100).toFixed(2)} & approve`}
                  helperText={<>Saving your card approves this design and charges <b>${(orderState.priceCents / 100).toFixed(2)}</b> ({orderState.quantity} pieces).</>}
                />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Charged → send to printer */}
      {order && status === 'charged' && (
        <div style={card}>
          <h3 style={{ marginTop: 0 }}>4. Payment complete</h3>
          <p style={{ color: '#64748b' }}>Your card was charged and the mail list ({(orderState.frozenList?.count ?? (orderState.frozenVoterIds || []).length).toLocaleString()} addresses) is locked in.</p>
          <button onClick={sendToPrinter} disabled={busy} style={btn(busy)}>Send to printer</button>
        </div>
      )}

      {order && status === 'sent_to_printer' && (
        <div style={{ ...card, background: '#f0fdf4', border: '1px solid #bbf7d0' }}>
          <h3 style={{ marginTop: 0, display: 'flex', alignItems: 'center', gap: 8, color: '#166534' }}><Icon name="check-circle" size={17} /> Sent to printer</h3>
          <p style={{ margin: 0 }}>Your postcard mailer is on its way to production. We'll print and mail {orderState.quantity} pieces.</p>
        </div>
      )}

      {/* Charge in flight (or interrupted) — the server finishes it without charging twice */}
      {order && status === 'approving' && (
        <div style={{ ...card, background: '#eff6ff', border: '1px solid #bfdbfe' }}>
          <h3 style={{ marginTop: 0 }}>Processing your payment</h3>
          <p style={{ color: '#334155' }}>
            We're locking in your mail list and charging your card. This usually takes under a minute, and you will never be charged twice for this order.
          </p>
          <button onClick={checkApproval} disabled={busy} style={btn(busy)}>{busy ? 'Checking…' : 'Check status'}</button>
        </div>
      )}

      {order && status === 'charge_failed' && (
        <div style={{ ...card, background: '#fef2f2', border: '1px solid #fecaca' }}>
          <h3 style={{ marginTop: 0 }}>Payment failed</h3>
          <p>{orderState.chargeError || 'The card was declined.'} Your card was not charged. Add a different card to try again.</p>
          {retryCard && order.setupIntentClientSecret ? (
            <div style={{ maxWidth: 480 }}>
              <SaveCardForm
                order={order}
                onSaved={onCardSaved}
                onError={setErr}
                buttonLabel={`Pay $${(orderState.priceCents / 100).toFixed(2)} & approve`}
                helperText={<>Saving this card charges <b>${(orderState.priceCents / 100).toFixed(2)}</b> ({orderState.quantity} pieces).</>}
              />
            </div>
          ) : (
            <button onClick={startCardRetry} disabled={busy} style={btn(busy, ACCENT)}>Use a different card</button>
          )}
        </div>
      )}
    </div>
  );
};

const PostcardsPage = () => (
  <Elements stripe={stripePromise}>
    <PostcardsPageInner />
  </Elements>
);

export default PostcardsPage;
